/**
 * DataQualityView -- Data Quality Assessment (conformance) screen.
 *
 * Run data quality checks triggers apply-mappings then loads quarantine results in-page (inline
 * processing banner, no route swap). Results scroll into view when ready.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Download,
  ArrowLeft,
  ArrowRight,
  RefreshCw,
  CheckCircle2,
  PlayCircle,
  ScrollText,
} from "lucide-react";
import { DqxTable } from "@/components/data-quality/DqxTable";
import { DqxSummaryPanel } from "@/components/data-quality/DqxSummaryPanel";
import { PageBanner } from "@/components/reusable/PageBanner";
import { Button, ConfirmDialog, Spinner } from "@/components/reusable";
import { useUpload } from "@/hooks/useUpload";
import {
  filterMappingsForRegion,
  getSelectedMappingWorksheets,
  selectWorksheetForRegion,
} from "@/lib/mappingRegions";
import {
  resolveDqxPreviewHydration,
  shouldShowQuarantineReconstructBanner,
} from "@/lib/pipelineDqxNavigation";
import { resolveApplyMappingTargetCol } from "@/lib/applyMappingsPayload";
import { useTelemetry } from "@/hooks/useTelemetry";
import { cn } from "@/lib/utils";
import {
  applyMappings,
  fetchDataContractRulesBySchema,
  fetchDqxQuarantineResults,
  fetchDqxValidationJobStatus,
  fetchSchemaFields,
  revalidateManualCleanse,
} from "@/services/api";
import {
  generateMappedGridCsv,
  generateErrorReportCsv,
  generateDataContractRulesCsv,
  downloadCsv,
} from "@/services/dqxExport";
import {
  buildContractRuleLookup,
  buildColumnAllowedValuesLookup,
} from "@/services/dqxContractRulesLookup";
import {
  deriveSourceGridFromQuarantine,
  quarantineRowsToDqxValidationResult,
} from "@/services/dqxQuarantineMapper";
import { applyReferenceListNormalizationToGrid } from "@/services/dqxReferenceListNormalization";
import {
  computeDqxRowIndicesByCheckType,
  computeDqxRowLevelSummary,
  DQX_SUMMARY_CHECK_TYPE_ORDER,
  type DqxSummaryCheckTypeBucket,
} from "@/services/dqxSummaryMetrics";
import {
  buildDqxRegionFilterOptions,
  filterQuarantineTableByRegion,
  intersectRowIndexFilters,
  rowIndicesForRegionFilter,
  type BoardDqxRunRef,
  type DqxRegionFilterOption,
} from "@/lib/dqxMergedQuarantine";
import type { ContractRulesSummaryStatus } from "@/components/data-quality/DqxSummaryPanel";
import type { CellEdit, DqxValidationResult, DqxColumnMapping, SchemaField } from "@/types";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { buildRegionSheetLabelLookup, regionLabelForRun } from "@/lib/regionSheetLabels";
import { PipelineMultiTableDqxChrome } from "@/components/upload/pipeline/PipelineMultiTableDqxChrome";
import {
  clampPipelineDqxDialogRunIndex,
  resolvePipelineDqxRowIndexBase,
} from "@/lib/pipelineMultiTableDqxView";
import { classifyDqxRunStatus } from "@/lib/dqxRunStatus";

// Roles permitted to edit cells (Admin and Schema Creator). Gate stays
// commented until the backend applies require_permission(CLEANSE_EDIT) to
// manual-cleanse routes.
// const CLEANSE_EDIT_ROLES = ["App.ADMIN", "App.SCHEMA_CREATOR"] as const;

const DQX_STATUS_POLL_MS = 5000;

function normalizeHeaderKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");
}

/** Prominent in-page state while apply-mappings + quarantine fetch run (no full-page takeover). */
function DqxProcessingBanner() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      role="status"
      aria-live="polite"
      className="mb-6 rounded-xl border border-[var(--color-brand-primary)]/30 bg-[var(--color-brand-primary)]/5 px-5 py-4 flex items-start gap-4"
    >
      <Spinner size="md" className="shrink-0 mt-0.5" />
      <div className="min-w-0 space-y-1">
        <p className="text-sm font-semibold text-[var(--color-text-primary)]">
          Data quality checks are running
        </p>
        <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
          Your file is being validated against the target schema. This usually takes a moment — you
          don&apos;t need to do anything.
        </p>
      </div>
    </motion.div>
  );
}

/** Inline notice when DQX results are retrieved and the run is clean. */
function DqxSuccessNotice() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="rounded-xl border border-emerald-200 bg-emerald-50/80 px-5 py-4 mb-6"
      role="status"
    >
      <div className="flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" aria-hidden />
        <div className="space-y-2">
          <p className="text-sm font-semibold text-emerald-950">No errors are present</p>
          <p className="text-sm text-emerald-900/90 leading-relaxed">
            Your data passed this assessment. You can proceed to the next step —{" "}
            <strong className="text-emerald-950">Known Mappings</strong> — using the Proceed button
            below. Your data will be written to the{" "}
            <strong className="text-emerald-950">gold layer</strong> automatically.
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// ── Main Page ─────────────────────────────────────────────────

/** Identity of a freshly triggered manual-cleanse DQ rerun. */
export interface DqxRerunStartedEvent {
  fileId: string;
  runId: number;
  jobId: string;
  /** When the user had a region filter selected, the board replaces that run. */
  regionId?: string | null;
  regionFilterKey?: string | null;
}

export interface DataQualityViewProps {
  /** Load results immediately (pipeline "View DQ results" flow). */
  autoRetrieve?: boolean;
  /** Start with the Retrieve button enabled (job already known to be finished). */
  startWithStatusCheckSucceeded?: boolean;
  /**
   * Pad body content and pin the PageBanner when rendered in the pipeline DQ
   * modal (scroll shell has no top padding so sticky top-0 has no gap).
   */
  dialogInset?: boolean;
  /**
   * Called when a manual-cleanse rerun has been triggered, with the run's
   * identity. The host owns the board state transition and polling — this
   * view never touches board state itself.
   */
  onDqxRerunStarted: (rerun: DqxRerunStartedEvent) => void;
  onExitToBoard: () => void;
  onProceedToKnownMappings: () => void;
  /**
   * Pipeline-dialog overrides for the file/schema being viewed. They take
   * precedence over the upload context, which can be stale or empty when the
   * extraction context could not be hydrated (artifacts are released once DQ
   * is clean / Published) — the view then renders from quarantine data alone.
   */
  boardFileId?: string | null;
  boardSchemaId?: string | null;
  /**
   * Per-region DQX jobs for the pipeline file. When more than one job is
   * present, View DQ shows one table at a time with Previous/Next navigation.
   */
  boardDqxRuns?: BoardDqxRunRef[];
  /** Which table to show first when opening multi-table DQ (0-based). */
  initialTableIndex?: number;
}

export function DataQualityView({
  autoRetrieve = false,
  startWithStatusCheckSucceeded = false,
  dialogInset = false,
  onDqxRerunStarted,
  onExitToBoard,
  onProceedToKnownMappings,
  boardFileId = null,
  boardSchemaId = null,
  boardDqxRuns = [],
  initialTableIndex = 0,
}: DataQualityViewProps) {
  const {
    worksheets,
    savedMappings,
    schemaId,
    reportType,
    schemaName,
    fileName,
    documentId,
    dqxRunId,
    dqxJobId,
    dqxTriggerMessage,
    setDqxRunInfo,
    isMultiRegion,
    mappingSelection,
    confirmUpload,
    saveMappings,
  } = useUpload();
  const { track, trackError, startTimer } = useTelemetry();

  const [validation, setValidation] = useState<DqxValidationResult | null>(null);
  /** True while apply-mappings and/or quarantine fetch are in progress (inline UI, not a route change). */
  const [dqxProcessing, setDqxProcessing] = useState(false);
  const [dqxStatusMessage, setDqxStatusMessage] = useState<string | null>(null);
  const conformanceResultsRef = useRef<HTMLDivElement>(null);
  const assessmentTopRef = useRef<HTMLDivElement>(null);
  const wasDqxProcessingRef = useRef(false);

  useEffect(() => {
    let scrollTimer: number | undefined;
    // Pipeline modal: keep the Assessment banner at the top of the view — do not jump to results.
    if (dialogInset) {
      wasDqxProcessingRef.current = dqxProcessing;
      return;
    }
    if (wasDqxProcessingRef.current && !dqxProcessing && validation) {
      scrollTimer = window.setTimeout(() => {
        conformanceResultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
    wasDqxProcessingRef.current = dqxProcessing;
    return () => {
      if (scrollTimer !== undefined) clearTimeout(scrollTimer);
    };
  }, [dialogInset, dqxProcessing, validation]);
  const [validationLoadMessage, setValidationLoadMessage] = useState<string | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  /** True after the last "Check DQX Status" returned a successful finished run (e.g. SUCCESS). */
  const [dqxStatusCheckSucceeded, setDqxStatusCheckSucceeded] = useState(
    startWithStatusCheckSucceeded
  );

  const [retrievingResults, setRetrievingResults] = useState(false);
  /** True only when Retrieve DQX Results was explicitly triggered and succeeded. */
  const [retrievedResultsByAction, setRetrievedResultsByAction] = useState(false);
  const [downloadingContractRules, setDownloadingContractRules] = useState(false);
  const [schemaFields, setSchemaFields] = useState<SchemaField[]>([]);
  const canDownloadContractRules = Boolean(schemaId?.trim());

  // fileId/schemaId are server-resolved and used to drive the deep-link
  // schemaFields fetch when there's no upload session.
  const [quarantinePayload, setQuarantinePayload] = useState<{
    table: Record<string, unknown>[];
    summaryTable: Record<string, unknown>[];
    fileId: string | null;
    schemaId: string | null;
  } | null>(null);
  /** `undefined` = contract rules still loading; `null` = load failed; `Map` = success (normalized keys). */
  const [dqxContractRuleLookup, setDqxContractRuleLookup] = useState<
    ReturnType<typeof buildContractRuleLookup> | null | undefined
  >(undefined);
  const [columnAllowedValuesLookup, setColumnAllowedValuesLookup] = useState<
    Map<string, string[]> | null | undefined
  >(undefined);
  const [dqxSummaryContractStatus, setDqxSummaryContractStatus] =
    useState<ContractRulesSummaryStatus>("idle");
  /** Multi-select filters for conformance grid (check-type buckets from summary). */
  const [selectedCheckTypeBuckets, setSelectedCheckTypeBuckets] = useState<
    DqxSummaryCheckTypeBucket[]
  >([]);
  /** Region filter key for cleanse re-run scoping. `"all"` = no region scoping. */
  const [selectedRegionKey, setSelectedRegionKey] = useState<string>("all");
  const [regionFilterOptions, setRegionFilterOptions] = useState<DqxRegionFilterOption[]>([]);
  const [rowIndexToRegionKey, setRowIndexToRegionKey] = useState<Map<number, string> | null>(null);
  /** When true, prefer quarantine-derived grid over worksheet remap. */
  const [preferQuarantineGrid, setPreferQuarantineGrid] = useState(false);
  /** True when extraction preview could not be reloaded (refresh / artifacts gone). */
  const [previewHydrateFailed, setPreviewHydrateFailed] = useState(false);
  const previewHydrateAttemptedRef = useRef(false);
  const [activeTableIndex, setActiveTableIndex] = useState(() =>
    clampPipelineDqxDialogRunIndex(initialTableIndex, boardDqxRuns.length || 1)
  );
  const retrieveGenerationRef = useRef(0);
  // Audit buffer for the current session, keyed "rowIndex-colIndex" to
  // match DqxValidationResult.cellErrors.
  const [cellEdits, setCellEdits] = useState<Map<string, CellEdit>>(new Map());
  const currentUser = useCurrentUser();

  const multiJobRuns = useMemo(
    () => boardDqxRuns.filter((run) => Boolean(run.jobId?.trim())),
    [boardDqxRuns]
  );
  const isMultiTableDqx = multiJobRuns.length > 1;

  const regionSheetLookup = useMemo(
    () => buildRegionSheetLabelLookup(worksheets, mappingSelection),
    [worksheets, mappingSelection]
  );

  const runLabels = useMemo(
    () =>
      multiJobRuns.map((run, index) => regionLabelForRun(run.regionId, index, regionSheetLookup)),
    [multiJobRuns, regionSheetLookup]
  );

  const activeTableRun = multiJobRuns[activeTableIndex] ?? null;
  const rowIndexBase = useMemo(
    () => resolvePipelineDqxRowIndexBase(multiJobRuns, activeTableIndex),
    [multiJobRuns, activeTableIndex]
  );

  // Manual cleanse available when a signed-in user is present.
  // Role gate disabled until Azure AD app-role RBAC is provisioned.
  const canManuallyCleanse = useMemo(() => {
    if (!currentUser) return false;

    // if (currentUser.roles.length === 0) return true;
    // return CLEANSE_EDIT_ROLES.some((r) => currentUser.roles.includes(r));

    return true;
  }, [currentUser]);

  const handleCellEdited = useCallback(
    (edit: Omit<CellEdit, "userId" | "userEmail" | "timestamp" | "status">) => {
      if (!currentUser) return;
      const full: CellEdit = {
        ...edit,
        userId: currentUser.userId,
        userEmail: currentUser.userEmail,
        timestamp: new Date().toISOString(),
        status: "pending",
      };
      setCellEdits((prev) => {
        const next = new Map(prev);
        next.set(edit.cellKey, full);
        return next;
      });
    },
    [currentUser]
  );

  const handleCellReverted = useCallback((cellKey: string) => {
    setCellEdits((prev) => {
      if (!prev.has(cellKey)) return prev;
      const next = new Map(prev);
      next.delete(cellKey);
      return next;
    });
  }, []);

  const pendingEditCount = useMemo(() => {
    let n = 0;
    for (const edit of cellEdits.values()) if (edit.status === "pending") n += 1;
    return n;
  }, [cellEdits]);

  // Target-schema display order lookup used by the Data Quality grid headers.
  // We index both field IDs and field names so normal session rows (names)
  // and deep-link quarantine rows (often IDs) can share the same ordering.
  const schemaHeaderOrder = useMemo(() => {
    const byFieldId = new Map<string, number>();
    const byFieldName = new Map<string, number>();

    schemaFields.forEach((field, index) => {
      const id = field.id?.trim();
      const name = field.fieldName?.trim();

      if (id) {
        byFieldId.set(id, index);
        byFieldId.set(id.toLowerCase(), index);
        byFieldId.set(normalizeHeaderKey(id), index);
      }

      if (name) {
        byFieldName.set(name, index);
        byFieldName.set(name.toLowerCase(), index);
        byFieldName.set(normalizeHeaderKey(name), index);
      }
    });

    return { byFieldId, byFieldName };
  }, [schemaFields]);

  // After each new validation, transition pending edits to pass/fail based on
  // whether their cell still appears in cellErrors.
  useEffect(() => {
    if (!validation) return;
    let passed = 0;
    let failed = 0;
    setCellEdits((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Map(prev);
      for (const [key, edit] of prev) {
        if (edit.status !== "pending") continue;
        const stillFailing = validation.cellErrors.has(key);
        if (stillFailing) failed += 1;
        else passed += 1;
        next.set(key, {
          ...edit,
          status: stillFailing ? "revalidated-fail" : "revalidated-pass",
        });
        changed = true;
      }
      return changed ? next : prev;
    });
    if (passed + failed > 0) {
      track("ManualCleanse.RevalidateResult", { passed, failed });
    }
  }, [validation, track]);

  const dqxStatusPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dqxRunIdRef = useRef(dqxRunId);
  dqxRunIdRef.current = dqxRunId;
  const dataQualityPageMountedRef = useRef(true);

  const clearDqxStatusPoll = useCallback(() => {
    if (dqxStatusPollRef.current !== null) {
      clearInterval(dqxStatusPollRef.current);
      dqxStatusPollRef.current = null;
    }
  }, []);

  useEffect(() => {
    dataQualityPageMountedRef.current = true;
    return () => {
      dataQualityPageMountedRef.current = false;
      clearDqxStatusPoll();
    };
  }, [clearDqxStatusPoll]);

  // After refresh the upload session is empty. Reload preview so multi-table
  // Results can remap worksheets instead of reconstructing from quarantine.
  useEffect(() => {
    const fileId = (boardFileId ?? documentId)?.trim();
    if (!fileId || worksheets.length > 0 || previewHydrateAttemptedRef.current) {
      return;
    }
    previewHydrateAttemptedRef.current = true;
    let cancelled = false;
    void (async () => {
      try {
        const hydrated = await resolveDqxPreviewHydration({
          fileId,
          savedMappings: savedMappings.length > 0 ? savedMappings : null,
        });
        if (cancelled) return;
        if (!hydrated) {
          setPreviewHydrateFailed(true);
          return;
        }
        const resolvedSchemaId = (boardSchemaId ?? schemaId)?.trim() || "";
        confirmUpload({
          file: new File([], fileName || fileId),
          fileName: fileName || fileId,
          documentId: fileId,
          schemaId: resolvedSchemaId,
          schemaName: schemaName || resolvedSchemaId || fileId,
          worksheets: hydrated.worksheets,
          mappingSelection: hydrated.mappingSelection,
          reportType,
        });
        saveMappings(hydrated.savedMappings);
        setPreviewHydrateFailed(false);
      } catch (err) {
        if (cancelled) return;
        setPreviewHydrateFailed(true);
        trackError(err, { context: "dqx_preview_self_hydrate", fileId });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    boardFileId,
    boardSchemaId,
    confirmUpload,
    documentId,
    fileName,
    reportType,
    saveMappings,
    savedMappings,
    schemaId,
    schemaName,
    trackError,
    worksheets.length,
  ]);

  // Worksheet data — for multi-table DQ show only the active region's rows.
  const mappingWorksheets = useMemo(() => {
    if (Object.keys(mappingSelection).length > 0) {
      return getSelectedMappingWorksheets(worksheets, mappingSelection);
    }
    return worksheets.filter(
      (w) =>
        !w.isMetadata && w.previewCategory !== "non_mapping" && w.previewCategory !== "ambiguity"
    );
  }, [worksheets, mappingSelection]);
  const activeRegionId = isMultiTableDqx ? (activeTableRun?.regionId ?? null) : null;
  const scopedSavedMappings = useMemo(
    () => (activeRegionId ? filterMappingsForRegion(savedMappings, activeRegionId) : savedMappings),
    [savedMappings, activeRegionId]
  );
  const columnMappings = useMemo<DqxColumnMapping[]>(
    () =>
      scopedSavedMappings.map((m) => ({
        sourceColumn: m.sourceField,
        targetColumn: m.targetField,
      })),
    [scopedSavedMappings]
  );
  const combinedWorksheet = useMemo(() => {
    if (isMultiTableDqx) {
      const scoped = selectWorksheetForRegion(mappingWorksheets, activeRegionId, {
        tableIndex: activeTableIndex,
      });
      if (scoped) {
        return {
          headers: scoped.headers,
          rows: scoped.rows,
        };
      }
    }
    if (!isMultiRegion || mappingWorksheets.length <= 1) {
      const ws = mappingWorksheets[0] ?? worksheets.find((w) => !w.isMetadata) ?? worksheets[0];
      return {
        headers: ws?.headers ?? [],
        rows: ws?.rows ?? [],
      };
    }
    // Legacy multi-region without per-table DQ jobs: keep prior concat behavior.
    const headerSet = new Set<string>();
    const rows: Record<string, unknown>[] = [];
    for (const ws of mappingWorksheets) {
      for (const header of ws.headers) headerSet.add(header);
      rows.push(...ws.rows);
    }
    return { headers: Array.from(headerSet), rows };
  }, [
    isMultiTableDqx,
    activeRegionId,
    activeTableIndex,
    isMultiRegion,
    mappingWorksheets,
    worksheets,
  ]);
  const ws = mappingWorksheets[0] ?? worksheets.find((w) => !w.isMetadata) ?? worksheets[0];
  const sourceHeaders = combinedWorksheet.headers;
  const sourceRows = combinedWorksheet.rows;
  // Normal session: remap worksheet rows through savedMappings.
  // Deep-link with no worksheet: synthesise from quarantine input_data
  // (failing rows only, headers keyed by source field id).
  const mappedGrid = useMemo(() => {
    const orderHeadersBySchema = (inputHeaders: string[], fromQuarantine: boolean): string[] => {
      if (inputHeaders.length <= 1) return inputHeaders;
      if (schemaHeaderOrder.byFieldId.size === 0 && schemaHeaderOrder.byFieldName.size === 0) {
        return inputHeaders;
      }

      const indexed = inputHeaders.map((header, index) => {
        const trimmed = header.trim();
        const lower = trimmed.toLowerCase();
        const normalized = normalizeHeaderKey(trimmed);
        const order =
          (fromQuarantine
            ? (schemaHeaderOrder.byFieldId.get(trimmed) ??
              schemaHeaderOrder.byFieldId.get(lower) ??
              schemaHeaderOrder.byFieldId.get(normalized) ??
              schemaHeaderOrder.byFieldName.get(trimmed) ??
              schemaHeaderOrder.byFieldName.get(lower) ??
              schemaHeaderOrder.byFieldName.get(normalized))
            : (schemaHeaderOrder.byFieldName.get(trimmed) ??
              schemaHeaderOrder.byFieldName.get(lower) ??
              schemaHeaderOrder.byFieldName.get(normalized) ??
              schemaHeaderOrder.byFieldId.get(trimmed) ??
              schemaHeaderOrder.byFieldId.get(lower) ??
              schemaHeaderOrder.byFieldId.get(normalized))) ?? Number.POSITIVE_INFINITY;

        return { header, index, order };
      });

      indexed.sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return a.index - b.index;
      });

      return indexed.map((x) => x.header);
    };

    if (!preferQuarantineGrid && sourceRows.length > 0) {
      const pairs = scopedSavedMappings
        .map((m) => ({
          source: m.sourceField?.trim(),
          target: m.targetField?.trim(),
        }))
        .filter((p): p is { source: string; target: string } => Boolean(p.source && p.target));

      const seenTargets = new Set<string>();
      const headers: string[] = [];
      for (const p of pairs) {
        if (!seenTargets.has(p.target)) {
          seenTargets.add(p.target);
          headers.push(p.target);
        }
      }

      const rows = sourceRows.map((row) => {
        const out: Record<string, unknown> = {};
        for (const p of pairs) out[p.target] = row[p.source];
        return out;
      });

      return { headers: orderHeadersBySchema(headers, false), rows, fromQuarantine: false };
    }

    if (quarantinePayload?.table?.length) {
      const derived = deriveSourceGridFromQuarantine(quarantinePayload.table, {
        schemaFields,
        savedMappings: scopedSavedMappings,
        rowIndexBase,
      });
      return {
        headers: orderHeadersBySchema(derived.headers, true),
        rows: derived.rows,
        fromQuarantine: true,
      };
    }

    return {
      headers: [] as string[],
      rows: [] as Record<string, unknown>[],
      fromQuarantine: false,
    };
  }, [
    sourceRows,
    scopedSavedMappings,
    quarantinePayload,
    schemaHeaderOrder,
    preferQuarantineGrid,
    schemaFields,
    rowIndexBase,
  ]);

  /** After DQX results are retrieved, show post-normalisation values (matches backend validation). */
  const rowsForResults = useMemo(() => {
    if (!retrievedResultsByAction) return mappedGrid.rows;
    if (!columnAllowedValuesLookup || columnAllowedValuesLookup.size === 0) {
      return mappedGrid.rows;
    }
    return applyReferenceListNormalizationToGrid(
      mappedGrid.rows,
      mappedGrid.headers,
      columnAllowedValuesLookup,
      schemaFields,
      scopedSavedMappings
    );
  }, [
    mappedGrid,
    retrievedResultsByAction,
    columnAllowedValuesLookup,
    schemaFields,
    scopedSavedMappings,
  ]);

  const headers = mappedGrid.headers;
  const rows = rowsForResults;
  /** Mapped highlight grid gate: status SUCCESS, then Retrieve DQX Results. */
  const canShowDqxResultsPanels =
    dqxStatusCheckSucceeded && retrievedResultsByAction && validation !== null;

  // Prefer upload-context ids; fall back to the quarantine response so a
  // deep-link page (no upload session) can still drive the schema-fields
  // fetch and revalidation.
  const effectiveSchemaId = useMemo(() => {
    return boardSchemaId?.trim() || schemaId?.trim() || quarantinePayload?.schemaId?.trim() || "";
  }, [boardSchemaId, schemaId, quarantinePayload]);

  const effectiveFileId = useMemo(() => {
    if (boardFileId?.trim()) return boardFileId.trim();
    if (documentId?.trim()) return documentId.trim();
    if (quarantinePayload?.fileId?.trim()) return quarantinePayload.fileId.trim();
    // Backwards compat: older sidecars don't surface fileId at the top level.
    const t = quarantinePayload?.table;
    if (!t?.length) return "";
    for (const row of t) {
      const v = row.file_id ?? row.fileId ?? row.FILE_ID;
      if (typeof v === "string" && v.trim()) return v.trim();
    }
    return "";
  }, [boardFileId, documentId, quarantinePayload]);

  useEffect(() => {
    if (!effectiveSchemaId) {
      setSchemaFields([]);
      return;
    }
    let cancelled = false;
    fetchSchemaFields(effectiveSchemaId)
      .then((fields) => {
        if (!cancelled) setSchemaFields(fields);
      })
      .catch(() => {
        if (!cancelled) setSchemaFields([]);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveSchemaId]);

  // Derived assessment: rebuild whenever the payload or a late-arriving input
  // (schema fields, context worksheets) changes, so fetch order never decides
  // whether failed cells get bridged to columns.
  useEffect(() => {
    if (!quarantinePayload) return;
    setValidation(
      quarantineRowsToDqxValidationResult(quarantinePayload.table, {
        headers,
        rows,
        savedMappings: scopedSavedMappings,
        schemaFields,
        contractRuleLookup: undefined,
        rowIndexBase,
      })
    );
  }, [quarantinePayload, headers, rows, scopedSavedMappings, schemaFields, rowIndexBase]);

  const canRunDqxChecks = useMemo(() => {
    if (!documentId?.trim() || savedMappings.length === 0 || !schemaId) return false;
    return savedMappings.every((m) => {
      if (m.targetFieldId) return true;
      return schemaFields.some((f) => f.fieldName === m.targetField);
    });
  }, [documentId, savedMappings, schemaId, schemaFields]);

  // ── DQX job id: reset messaging (quarantine data loads only via "Retrieve DQX Results") ──

  useEffect(() => {
    if (!dqxJobId) {
      setValidation(null);
      setQuarantinePayload(null);
      setDqxContractRuleLookup(undefined);
      setColumnAllowedValuesLookup(undefined);
      setDqxSummaryContractStatus("idle");
      setSelectedCheckTypeBuckets([]);
      setValidationLoadMessage(
        "Run data quality checks below to validate your mapped file against the schema."
      );
    }
  }, [dqxJobId]);

  useEffect(() => {
    setRetrievedResultsByAction(false);
    setQuarantinePayload(null);
    setDqxContractRuleLookup(undefined);
    setColumnAllowedValuesLookup(undefined);
    setDqxSummaryContractStatus("idle");
    setSelectedCheckTypeBuckets([]);
  }, [dqxJobId]);

  const handleRunDqxChecks = useCallback(async () => {
    if (!documentId || !schemaId || savedMappings.length === 0 || !canRunDqxChecks) return;

    if (isMultiRegion && dqxRunId) {
      setValidationLoadMessage(
        "Data quality checks were already triggered after the final mapping region was saved."
      );
      return;
    }

    setDqxRunInfo({ runId: null, jobId: "", message: "" });
    setDqxStatusMessage(null);
    setDqxStatusCheckSucceeded(false);
    setRetrievedResultsByAction(false);
    setValidation(null);
    setQuarantinePayload(null);
    setDqxContractRuleLookup(undefined);
    setColumnAllowedValuesLookup(undefined);
    setDqxSummaryContractStatus("idle");
    setSelectedCheckTypeBuckets([]);
    setDqxProcessing(true);
    setValidationLoadMessage(null);

    const stopTimer = startTimer();
    try {
      const mappings = savedMappings.map((m) => ({
        source_col: m.sourceField,
        target_col: resolveApplyMappingTargetCol(m, schemaFields),
        ...(m.regionId ? { region_id: m.regionId } : {}),
      }));
      const result = await applyMappings({
        fileId: documentId,
        schemaId,
        reportType: reportType ?? null,
        mappings,
        multiRegion: isMultiRegion,
      });
      setDqxRunInfo({
        runId: result.dqx_run_id ?? null,
        jobId: result.dqx_job_id ?? "",
        message: result.dqx_trigger_message ?? "",
      });
      setDqxProcessing(false);
      track(
        "Dqx.RunChecks",
        {
          fileName: fileName || "unknown",
          fileId: documentId,
          schemaId,
          mappingCount: mappings.length,
          ...(result.dqx_run_id != null ? { dqxRunId: result.dqx_run_id } : {}),
        },
        { durationMs: stopTimer() }
      );
    } catch (err) {
      setDqxRunInfo({
        runId: null,
        jobId: "",
        message: `Failed to start data quality checks: ${err instanceof Error ? err.message : String(err)}`,
      });
      setDqxProcessing(false);
      trackError(err, { context: "dqx_apply_mappings", documentId });
    }
  }, [
    documentId,
    schemaId,
    reportType,
    savedMappings,
    schemaFields,
    isMultiRegion,
    dqxRunId,
    canRunDqxChecks,
    setDqxRunInfo,
    fileName,
    startTimer,
    track,
    trackError,
  ]);

  // No pending edits -> fall through to a plain re-run.
  const handleRevalidateWithEdits = useCallback(async () => {
    const pending: CellEdit[] = [];
    for (const e of cellEdits.values()) if (e.status === "pending") pending.push(e);
    if (pending.length === 0) {
      await handleRunDqxChecks();
      return;
    }
    if (!effectiveFileId || !effectiveSchemaId) return;

    setDqxRunInfo({ runId: null, jobId: "", message: "" });
    setDqxStatusMessage(null);
    setDqxStatusCheckSucceeded(false);
    setRetrievedResultsByAction(false);
    setValidation(null);
    setQuarantinePayload(null);
    setDqxContractRuleLookup(undefined);
    setDqxSummaryContractStatus("idle");
    setSelectedCheckTypeBuckets([]);
    setDqxProcessing(true);
    setValidationLoadMessage(null);

    const stopTimer = startTimer();
    try {
      // Translate the table header (which the user actually saw) into the key
      // used inside the mapped_data JSON dict. Normal session: header is the
      // target column NAME; field id lives on savedMappings or schemaFields.
      // Deep-link: header is already a field id, so the lookups miss and we
      // pass through.
      const toFieldId = (header: string): string => {
        const m = savedMappings.find((sm) => sm.targetField === header);
        if (m?.targetFieldId) return m.targetFieldId;
        const sf = schemaFields.find((f) => f.fieldName === header);
        return sf?.id ?? header;
      };

      const result = await revalidateManualCleanse({
        fileId: effectiveFileId,
        schemaId: effectiveSchemaId,
        reportType: reportType ?? null,
        edits: pending.map((e) => ({
          cell_key: e.cellKey,
          row_index: e.rowIndex + rowIndexBase,
          column_index: e.columnIndex,
          source_column_name: toFieldId(e.columnName),
          old_value: e.oldValue,
          new_value: e.newValue,
          user_id: e.userId,
          user_email: e.userEmail,
          timestamp: e.timestamp,
        })),
      });
      setDqxRunInfo({
        runId: result.dqx_run_id ?? null,
        jobId: result.dqx_job_id ?? "",
        message: result.dqx_trigger_message ?? "",
      });
      setDqxProcessing(false);
      track(
        "ManualCleanse.RevalidateTriggered",
        {
          fileId: effectiveFileId,
          pendingCount: pending.length,
          cellsApplied: result.cells_applied,
          auditEntriesWritten: result.audit_entries_written,
          deepLink: savedMappings.length === 0,
          ...(result.dqx_run_id != null ? { dqxRunId: result.dqx_run_id } : {}),
        },
        { durationMs: stopTimer() }
      );

      const revalidateRunId = result.dqx_run_id;
      const revalidateJobId = result.dqx_job_id ?? "";
      if (revalidateRunId != null && revalidateJobId && effectiveFileId) {
        const regionOpt =
          selectedRegionKey !== "all"
            ? regionFilterOptions.find((o) => o.key === selectedRegionKey)
            : undefined;
        onDqxRerunStarted({
          fileId: effectiveFileId,
          runId: revalidateRunId,
          jobId: revalidateJobId,
          regionId: activeTableRun?.regionId ?? regionOpt?.regionId ?? null,
          regionFilterKey: selectedRegionKey !== "all" ? selectedRegionKey : null,
        });
      }
    } catch (err) {
      setDqxRunInfo({
        runId: null,
        jobId: "",
        message: `Failed to revalidate: ${err instanceof Error ? err.message : String(err)}`,
      });
      setDqxProcessing(false);
      trackError(err, { context: "manual_cleanse_revalidate", fileId: effectiveFileId });
    }
  }, [
    cellEdits,
    handleRunDqxChecks,
    effectiveFileId,
    effectiveSchemaId,
    onDqxRerunStarted,
    reportType,
    savedMappings,
    schemaFields,
    setDqxRunInfo,
    startTimer,
    track,
    trackError,
    selectedRegionKey,
    regionFilterOptions,
    activeTableRun,
    rowIndexBase,
  ]);

  // ── Download handlers ───────────────────────────────────────

  const handleDownloadRemapped = useCallback(() => {
    if (headers.length === 0) return;
    const csv = generateMappedGridCsv(headers, rows);
    const baseName = (fileName || "remapped").replace(/\.[^.]+$/, "");
    downloadCsv(csv, `${baseName}_remapped.csv`);

    track("Dqx.DownloadRemappedFile", {
      fileName: fileName || "unknown",
      rowCount: sourceRows.length,
      columnCount: sourceHeaders.length,
      mappingCount: columnMappings.length,
      hasErrors: validation?.hasErrors ?? false,
    });
  }, [
    headers,
    rows,
    fileName,
    sourceRows.length,
    sourceHeaders.length,
    columnMappings.length,
    validation,
    track,
  ]);

  const handleDownloadErrorReport = useCallback(() => {
    if (!validation) return;
    const csv = generateErrorReportCsv(validation.errorReport);
    const baseName = (fileName || "errors").replace(/\.[^.]+$/, "");
    downloadCsv(csv, `${baseName}_error_report.csv`);

    track("Dqx.DownloadErrorReport", {
      fileName: fileName || "unknown",
      errorCount: validation.errorReport.length,
      totalErrorCells: validation.totalErrorCells,
    });
  }, [validation, fileName, track]);

  const handleDownloadDataContractRules = useCallback(async () => {
    if (!schemaId?.trim()) return;
    setDownloadingContractRules(true);
    try {
      const data = await fetchDataContractRulesBySchema(schemaId.trim());
      const baseName = (fileName || "data_contract").replace(/\.[^.]+$/, "");
      const csv = generateDataContractRulesCsv(data);
      downloadCsv(csv, `${baseName}_data_contract_rules.csv`);
      track("Dqx.DownloadDataContractRules", {
        fileName: fileName || "unknown",
        schemaId: schemaId.trim(),
        contractName: data.contract_name,
        ruleCount: data.contract_rules.length,
      });
    } catch (err) {
      trackError(err, {
        context: "dqx_contract_rules_download",
        schemaId: schemaId.trim(),
      });
    } finally {
      setDownloadingContractRules(false);
    }
  }, [schemaId, fileName, track, trackError]);

  // ── Navigation handlers ─────────────────────────────────────

  const handleBackToUpload = useCallback(() => {
    track("Dqx.BackToUpload", {
      hadErrors: validation?.hasErrors ?? false,
      totalErrorCells: validation?.totalErrorCells ?? 0,
    });
    onExitToBoard();
  }, [onExitToBoard, validation, track]);

  const [pendingEditBlockOpen, setPendingEditBlockOpen] = useState(false);

  const handleProceed = useCallback(() => {
    if (!validation) return;
    if (pendingEditCount > 0) {
      track("ManualCleanse.ProceedBlocked", { pendingCount: pendingEditCount });
      setPendingEditBlockOpen(true);
      return;
    }
    track("Dqx.ProceedToKnownMappings", {
      fileName: fileName || "unknown",
      mappingCount: savedMappings.length,
    });
    onProceedToKnownMappings();
  }, [
    validation,
    pendingEditCount,
    onProceedToKnownMappings,
    fileName,
    savedMappings.length,
    track,
  ]);

  const handleCheckDqxStatus = useCallback(() => {
    const runId = dqxRunIdRef.current;
    if (!runId) return;

    clearDqxStatusPoll();
    setCheckingStatus(true);
    setDqxStatusMessage(null);
    setDqxStatusCheckSucceeded(false);

    const applyStatusToUi = (status: Awaited<ReturnType<typeof fetchDqxValidationJobStatus>>) => {
      setDqxStatusMessage(
        `Job ID ${status.runId}: ${status.lifeCycleState}${
          status.resultState ? ` / ${status.resultState}` : ""
        }`
      );
    };

    const tick = async (): Promise<boolean> => {
      const id = dqxRunIdRef.current;
      if (!id) {
        clearDqxStatusPoll();
        if (dataQualityPageMountedRef.current) setCheckingStatus(false);
        return true;
      }
      try {
        const status = await fetchDqxValidationJobStatus(id);
        if (!dataQualityPageMountedRef.current) return true;
        applyStatusToUi(status);
        const outcome = classifyDqxRunStatus(status);
        if (outcome === "running") return false;

        const succeeded = outcome === "success";
        setDqxStatusCheckSucceeded(succeeded);
        track("Dqx.StatusCheckClicked", {
          runId: status.runId,
          lifeCycleState: status.lifeCycleState,
          resultState: status.resultState ?? "PENDING",
          succeeded,
          terminalOutcome: outcome,
        });
        return true;
      } catch (err) {
        if (!dataQualityPageMountedRef.current) return true;
        trackError(err, { context: "dqx_status_check", runId: id });
        return false;
      }
    };

    void (async () => {
      const done = await tick();
      if (done) {
        if (dataQualityPageMountedRef.current) setCheckingStatus(false);
        return;
      }
      dqxStatusPollRef.current = setInterval(() => {
        void (async () => {
          const finished = await tick();
          if (finished) {
            clearDqxStatusPoll();
            if (dataQualityPageMountedRef.current) setCheckingStatus(false);
          }
        })();
      }, DQX_STATUS_POLL_MS);
    })();
  }, [clearDqxStatusPoll, track, trackError]);

  /** Rebuild grid validation when quarantine data or contract lookup changes (incl. async rules load). */
  useEffect(() => {
    if (!quarantinePayload) return;
    setValidation(
      quarantineRowsToDqxValidationResult(quarantinePayload.table, {
        headers,
        rows,
        savedMappings: scopedSavedMappings,
        schemaFields,
        contractRuleLookup: dqxContractRuleLookup,
        rowIndexBase,
      })
    );
  }, [
    quarantinePayload,
    headers,
    rows,
    scopedSavedMappings,
    schemaFields,
    dqxContractRuleLookup,
    rowIndexBase,
  ]);

  const dqxRowLevelSummary = useMemo(() => {
    if (!quarantinePayload) return null;
    const scopedTable = filterQuarantineTableByRegion(quarantinePayload.table, selectedRegionKey);
    // Use full grid length so remapped global row_ids still resolve for bucket indices.
    const base = computeDqxRowLevelSummary(
      scopedTable,
      rows.length,
      dqxContractRuleLookup,
      rowIndexBase
    );
    if (selectedRegionKey === "all") return base;
    const regionIdx = rowIndicesForRegionFilter(rowIndexToRegionKey, selectedRegionKey) ?? [];
    return {
      ...base,
      totalRowsChecked: regionIdx.length,
      rowsPassingAllChecks: Math.max(0, regionIdx.length - base.rowsWithAtLeastOneFailure),
      rowsWithAtLeastOneFailure: base.rowsWithAtLeastOneFailure,
    };
  }, [
    quarantinePayload,
    rows.length,
    dqxContractRuleLookup,
    selectedRegionKey,
    rowIndexToRegionKey,
    rowIndexBase,
  ]);

  const rowIndicesByCheckType = useMemo(() => {
    if (!quarantinePayload) return null;
    const scopedTable = filterQuarantineTableByRegion(quarantinePayload.table, selectedRegionKey);
    return computeDqxRowIndicesByCheckType(
      scopedTable,
      rows.length,
      dqxContractRuleLookup,
      rowIndexBase
    );
  }, [quarantinePayload, rows.length, dqxContractRuleLookup, selectedRegionKey, rowIndexBase]);

  const breakdownDisplayCounts = useMemo(() => {
    const empty = {} as Record<DqxSummaryCheckTypeBucket, number>;
    for (const k of DQX_SUMMARY_CHECK_TYPE_ORDER) {
      empty[k] = 0;
    }
    if (!dqxRowLevelSummary) return empty;
    return dqxRowLevelSummary.failingRowsByCheckType;
  }, [dqxRowLevelSummary]);

  const breakdownCellCounts = useMemo(() => {
    const counts = {} as Record<DqxSummaryCheckTypeBucket, number>;
    for (const k of DQX_SUMMARY_CHECK_TYPE_ORDER) counts[k] = 0;
    if (!validation) return counts;
    const regionIndices = rowIndicesForRegionFilter(rowIndexToRegionKey, selectedRegionKey);
    const regionSet = regionIndices ? new Set(regionIndices) : null;
    const seen = new Map<DqxSummaryCheckTypeBucket, Set<string>>();
    for (const r of validation.errorReport) {
      if (r.columnIndex < 0 || !r.bucket) continue;
      const zeroBasedRow = r.rowIndex - 1;
      if (regionSet && !regionSet.has(zeroBasedRow)) continue;
      const cellKey = `${zeroBasedRow}-${r.columnIndex}`; // errorReport rowIndex is 1-indexed
      // Edited cells drop out of the bucket count until re-validation runs.
      if (cellEdits.has(cellKey)) continue;
      const bucket = r.bucket as DqxSummaryCheckTypeBucket;
      if (!(bucket in counts)) continue;
      if (!seen.has(bucket)) seen.set(bucket, new Set());
      seen.get(bucket)!.add(cellKey);
    }
    for (const [bucket, cells] of seen) counts[bucket] = cells.size;
    return counts;
  }, [validation, cellEdits, rowIndexToRegionKey, selectedRegionKey]);

  const checkTypeVisibleRowIndices = useMemo(() => {
    if (selectedCheckTypeBuckets.length === 0) return undefined;
    if (!rowIndicesByCheckType) return [];
    const union = new Set<number>();
    for (const b of selectedCheckTypeBuckets) {
      for (const idx of rowIndicesByCheckType[b]) {
        union.add(idx);
      }
    }
    return Array.from(union).sort((a, b) => a - b);
  }, [selectedCheckTypeBuckets, rowIndicesByCheckType]);

  const regionVisibleRowIndices = useMemo(
    () => rowIndicesForRegionFilter(rowIndexToRegionKey, selectedRegionKey),
    [rowIndexToRegionKey, selectedRegionKey]
  );

  const conformanceVisibleRowIndices = useMemo(
    () => intersectRowIndexFilters(checkTypeVisibleRowIndices, regionVisibleRowIndices),
    [checkTypeVisibleRowIndices, regionVisibleRowIndices]
  );

  const toggleCheckTypeBucket = useCallback((bucket: DqxSummaryCheckTypeBucket) => {
    setSelectedCheckTypeBuckets((prev) =>
      prev.includes(bucket) ? prev.filter((x) => x !== bucket) : [...prev, bucket]
    );
  }, []);

  const clearCheckTypeSelections = useCallback(() => {
    setSelectedCheckTypeBuckets([]);
  }, []);

  const conformanceFilterActive = selectedCheckTypeBuckets.length > 0;
  const conformanceFilterEmpty =
    conformanceFilterActive &&
    conformanceVisibleRowIndices !== undefined &&
    conformanceVisibleRowIndices.length === 0;

  const rowLevelIssues = useMemo(() => {
    const all = validation?.errorReport ?? [];
    const filtered = (() => {
      if (!conformanceFilterActive) return all.filter((r) => r.columnIndex === -1);
      const visibleSet = conformanceVisibleRowIndices
        ? new Set(conformanceVisibleRowIndices)
        : null;
      return all.filter((r) => {
        if (visibleSet && !visibleSet.has(r.rowIndex - 1)) return false;
        if (r.bucket && !selectedCheckTypeBuckets.includes(r.bucket as never)) return false;
        return true;
      });
    })();
    const seen = new Set<string>();
    const deduped = filtered.filter((r) => {
      const key = `${r.rowIndex}||${r.error}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const sorted = [...deduped].sort((a, b) => a.rowIndex - b.rowIndex);
    const uniqueRows = [...new Set(sorted.map((r) => r.rowIndex))];
    const rowDisplayMap = new Map(uniqueRows.map((ri, i) => [ri, i + 1]));
    return sorted.map((r) => ({ ...r, displayRow: rowDisplayMap.get(r.rowIndex) ?? r.rowIndex }));
  }, [validation, conformanceFilterActive, conformanceVisibleRowIndices, selectedCheckTypeBuckets]);

  const handleRetrieveDqxResults = useCallback(async () => {
    const runsForMerge = multiJobRuns;
    const primaryJobId = dqxJobId?.trim() || runsForMerge[0]?.jobId?.trim() || "";
    if (!primaryJobId && runsForMerge.length === 0) return;

    const generation = retrieveGenerationRef.current + 1;
    retrieveGenerationRef.current = generation;

    setRetrievingResults(true);
    setValidationLoadMessage(null);
    const stopTimer = startTimer();
    try {
      const perTableIndex =
        runsForMerge.length > 1
          ? clampPipelineDqxDialogRunIndex(activeTableIndex, runsForMerge.length)
          : 0;
      const run = runsForMerge.length > 1 ? runsForMerge[perTableIndex] : runsForMerge[0];
      const jobId = run?.jobId?.trim() || primaryJobId;
      const quarantine = await fetchDqxQuarantineResults(jobId);
      if (retrieveGenerationRef.current !== generation) return;

      const table = quarantine.quarantine_table;
      const summaryTable = quarantine.quarantine_summary_table;
      const fileId = quarantine.file_id ?? null;
      const resolvedSchemaId = quarantine.schema_id ?? null;
      const trackJobId = jobId;

      setPreferQuarantineGrid(false);
      setRegionFilterOptions(
        runsForMerge.length >= 1
          ? buildDqxRegionFilterOptions(
              runsForMerge.length > 1 ? [runsForMerge[perTableIndex]!] : runsForMerge,
              runsForMerge.length > 1 ? [runLabels[perTableIndex]] : runLabels
            )
          : []
      );
      setRowIndexToRegionKey(null);
      setSelectedRegionKey("all");

      setQuarantinePayload({
        table,
        summaryTable,
        fileId,
        schemaId: resolvedSchemaId,
      });
      setRetrievedResultsByAction(true);
      setValidationLoadMessage(null);
      if (dialogInset) {
        requestAnimationFrame(() => {
          const shell = assessmentTopRef.current?.closest<HTMLElement>(
            "[class*='overflow-y-auto']"
          );
          if (shell) shell.scrollTop = 0;
          else assessmentTopRef.current?.scrollIntoView({ block: "start" });
        });
      }

      setDqxSummaryContractStatus("loading");
      setDqxContractRuleLookup(undefined);
      setColumnAllowedValuesLookup(undefined);
      void (async () => {
        const sid = schemaId?.trim() || resolvedSchemaId?.trim() || "";
        if (!sid) {
          setDqxContractRuleLookup(new Map());
          setColumnAllowedValuesLookup(new Map());
          setDqxSummaryContractStatus("success");
          return;
        }
        try {
          const cr = await fetchDataContractRulesBySchema(sid);
          setDqxContractRuleLookup(buildContractRuleLookup(cr.contract_rules));
          setColumnAllowedValuesLookup(buildColumnAllowedValuesLookup(cr.contract_rules));
          setDqxSummaryContractStatus("success");
        } catch {
          setDqxContractRuleLookup(null);
          setColumnAllowedValuesLookup(null);
          setDqxSummaryContractStatus("error");
        }
      })();

      const trackSnapshot = quarantineRowsToDqxValidationResult(table, {
        headers,
        rows,
        savedMappings: scopedSavedMappings,
        schemaFields,
        contractRuleLookup: undefined,
        rowIndexBase,
      });
      const durationMs = stopTimer();
      track(
        "Dqx.ResultsRetrieved",
        {
          fileName: fileName || "unknown",
          dqxJobId: trackJobId,
          hasErrors: trackSnapshot.hasErrors,
          totalErrorCells: trackSnapshot.totalErrorCells,
          rowCount: rows.length,
          columnCount: headers.length,
          mergedJobs: 1,
          viewMode: "per-table",
          tableIndex: perTableIndex,
        },
        { durationMs }
      );
      track(
        "Dqx.ValidationComplete",
        {
          fileName: fileName || "unknown",
          schemaId: schemaId || "unknown",
          schemaName: schemaName || "unknown",
          hasErrors: trackSnapshot.hasErrors,
          totalErrorCells: trackSnapshot.totalErrorCells,
          mappingCount: savedMappings.length,
          rowCount: rows.length,
          columnCount: headers.length,
          dqxJobId: trackJobId,
        },
        { durationMs }
      );
    } catch (err) {
      setValidation(null);
      setQuarantinePayload(null);
      setDqxContractRuleLookup(undefined);
      setColumnAllowedValuesLookup(undefined);
      setDqxSummaryContractStatus("idle");
      setRetrievedResultsByAction(false);
      setPreferQuarantineGrid(false);
      setRegionFilterOptions([]);
      setRowIndexToRegionKey(null);
      setValidationLoadMessage("Failed to fetch DQX quarantine results.");
      trackError(err, { context: "dqx_quarantine_retrieve", dqxJobId: primaryJobId });
    } finally {
      setRetrievingResults(false);
    }
  }, [
    dialogInset,
    dqxJobId,
    multiJobRuns,
    activeTableIndex,
    runLabels,
    headers,
    rows,
    scopedSavedMappings,
    savedMappings.length,
    schemaFields,
    schemaId,
    schemaName,
    fileName,
    rowIndexBase,
    startTimer,
    track,
    trackError,
  ]);

  const pipelineAutoRetrieveRef = useRef(false);
  const tableNavRefetchRef = useRef(false);

  useEffect(() => {
    setActiveTableIndex(
      clampPipelineDqxDialogRunIndex(initialTableIndex, multiJobRuns.length || 1)
    );
  }, [initialTableIndex, multiJobRuns.length]);

  useEffect(() => {
    if (!retrievedResultsByAction || !isMultiTableDqx || !tableNavRefetchRef.current) {
      return;
    }
    tableNavRefetchRef.current = false;
    void handleRetrieveDqxResults();
  }, [activeTableIndex, retrievedResultsByAction, isMultiTableDqx, handleRetrieveDqxResults]);

  const goToPreviousTable = useCallback(() => {
    tableNavRefetchRef.current = true;
    setCellEdits(new Map());
    setActiveTableIndex((index) => Math.max(0, index - 1));
  }, []);

  const goToNextTable = useCallback(() => {
    tableNavRefetchRef.current = true;
    setCellEdits(new Map());
    setActiveTableIndex((index) => clampPipelineDqxDialogRunIndex(index + 1, multiJobRuns.length));
  }, [multiJobRuns.length]);

  useEffect(() => {
    // Only job identity gates retrieval: the assessment renders from quarantine
    // data alone, so a missing upload context (artifacts cleaned after DQ
    // completion) must not leave the dialog empty.
    const hasJobs = Boolean(dqxJobId?.trim()) || multiJobRuns.length > 0;
    if (!autoRetrieve || pipelineAutoRetrieveRef.current || !hasJobs) return;

    pipelineAutoRetrieveRef.current = true;
    setDqxStatusCheckSucceeded(true);
    void handleRetrieveDqxResults();
  }, [autoRetrieve, dqxJobId, multiJobRuns.length, handleRetrieveDqxResults]);

  const hasValidation = validation !== null;
  const hasErrors = validation?.hasErrors ?? false;
  // Allow Proceed once the user has touched at least one cell. The pending-
  // edit modal still gates the navigation when re-validation hasn't run.
  const canProceed =
    validation !== null && (validation.totalErrorCells === 0 || cellEdits.size > 0);
  const displaySchemaName = schemaName || schemaId || "Lloyd's 5.2 BDX Schema";

  // ── Render ──────────────────────────────────────────────────

  return (
    <>
      <div ref={assessmentTopRef}>
        <PageBanner
          title="Data Quality Assessment"
          subtitle="Validate mapped data against schema rules"
          className={cn("!h-28", dialogInset ? "!mb-0 !rounded-none" : "!mb-4")}
        />
      </div>

      <div className={cn(dialogInset && "px-6 pt-4 pb-6")}>
        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 max-w-3xl"
        >
          {!hasValidation ? (
            <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
              {autoRetrieve
                ? retrievingResults
                  ? "Loading conformance results for this run…"
                  : dqxJobId
                    ? "Results for this data quality run will appear below shortly."
                    : "Run data quality checks to validate your mapped file."
                : dqxJobId
                  ? "When the run has finished successfully, use Check Status, then Retrieve Results to load the conformance grid and validation detail below."
                  : "Run data quality checks to validate your mapped file. After the run succeeds, check status and retrieve results to see the conformance grid."}
            </p>
          ) : hasErrors ? (
            <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
              It appears there were instances where the data in your re-mapped file went against the
              rules of the{" "}
              <strong className="text-[var(--color-text-primary)]">{displaySchemaName}</strong>{" "}
              target schema. Download your re-mapped file and make corrections based on the detailed
              Error Report. Once revisions have been made, return to the home screen and upload your
              revised file in order to proceed to the next stage.
            </p>
          ) : (
            <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
              The Data Quality Assessment is run against your mapped data for{" "}
              <strong className="text-[var(--color-text-primary)]">{displaySchemaName}</strong>.
              Conformance detail is shown in the validation section below after the run completes.
            </p>
          )}
        </motion.div>

        {dqxProcessing && <DqxProcessingBanner />}

        {!autoRetrieve &&
          (dqxTriggerMessage ||
            dqxRunId ||
            dqxStatusMessage ||
            validationLoadMessage ||
            checkingStatus) && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 rounded-xl border border-[var(--color-border-light)] bg-[var(--color-bg-secondary)] px-4 py-3"
            >
              {dqxTriggerMessage && (
                <p className="text-sm text-[var(--color-text-primary)]">{dqxTriggerMessage}</p>
              )}
              {!dqxTriggerMessage && dqxRunId && (
                <p className="text-sm text-[var(--color-text-primary)]">
                  DQX trigger accepted. Run ID: {dqxRunId}
                  {dqxJobId ? `, DQX Job ID: ${dqxJobId}` : ""}
                </p>
              )}
              {checkingStatus && (
                <div
                  className="flex items-center gap-2 mt-2 text-sm text-[var(--color-text-secondary)]"
                  role="status"
                  aria-live="polite"
                >
                  <Spinner size="sm" />
                  <span>
                    Checking Job Status (every {DQX_STATUS_POLL_MS / 1000}s until finished)…
                  </span>
                </div>
              )}
              {dqxStatusMessage && (
                <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                  {dqxStatusMessage}
                </p>
              )}
              {validationLoadMessage && (
                <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                  {validationLoadMessage}
                </p>
              )}
            </motion.div>
          )}

        {/* Auto-retrieve: surface load errors only (no job-triggered status pill). */}
        {autoRetrieve && validationLoadMessage ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 rounded-xl border border-[var(--color-border-light)] bg-[var(--color-bg-secondary)] px-4 py-3"
          >
            <p className="text-sm text-[var(--color-text-secondary)]">{validationLoadMessage}</p>
          </motion.div>
        ) : null}

        {/* Success notice (after Retrieve DQX Results) */}
        <AnimatePresence mode="wait">
          {canShowDqxResultsPanels && validation && !hasErrors && (
            <DqxSuccessNotice key="dqx-success" />
          )}
        </AnimatePresence>

        {/* Primary DQX actions — hidden for pipeline View DQ Results (autoRetrieve). */}
        {!autoRetrieve ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap gap-3 mb-6"
          >
            {!dqxJobId && (
              <Button
                variant="primary"
                size="md"
                onClick={handleRunDqxChecks}
                disabled={!canRunDqxChecks || dqxProcessing}
                className="gap-2 min-w-[220px]"
              >
                {dqxProcessing ? (
                  <>
                    <Spinner size="sm" className="!h-4 !w-4" />
                    Starting checks…
                  </>
                ) : (
                  <>
                    <PlayCircle className="w-4 h-4" />
                    Run data quality checks
                  </>
                )}
              </Button>
            )}
            <Button
              variant="secondary"
              size="md"
              onClick={handleCheckDqxStatus}
              disabled={!dqxRunId || checkingStatus}
              className="gap-2"
            >
              {checkingStatus ? (
                <>
                  <Spinner size="sm" />
                  Checking Job status…
                </>
              ) : (
                "Check Job Status"
              )}
            </Button>
            {dqxStatusCheckSucceeded && dqxJobId && (
              <Button
                variant="primary"
                size="md"
                onClick={handleRetrieveDqxResults}
                disabled={retrievingResults}
                className="gap-2"
              >
                {retrievingResults ? "Retrieving..." : "Retrieve Results"}
                <RefreshCw className={`w-4 h-4 ${retrievingResults ? "animate-spin" : ""}`} />
              </Button>
            )}
          </motion.div>
        ) : null}

        {canShowDqxResultsPanels && (
          <>
            {isMultiTableDqx ? (
              <PipelineMultiTableDqxChrome
                tableIndex={activeTableIndex}
                tableCount={multiJobRuns.length}
                sheetLabel={runLabels[activeTableIndex]}
                onPreviousTable={goToPreviousTable}
                onNextTable={goToNextTable}
              />
            ) : null}
            {dqxRowLevelSummary && (
              <DqxSummaryPanel
                summary={dqxRowLevelSummary}
                contractRulesStatus={dqxSummaryContractStatus}
                breakdownDisplayCounts={breakdownDisplayCounts}
                breakdownCellCounts={breakdownCellCounts}
                selectedBuckets={selectedCheckTypeBuckets}
                onToggleBucket={toggleCheckTypeBucket}
                onClearSelections={clearCheckTypeSelections}
              />
            )}
            <motion.div
              ref={conformanceResultsRef}
              id="dqx-conformance-results"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32 }}
              className={cn("mb-6", dialogInset ? "scroll-mt-32" : "scroll-mt-24")}
            >
              <p className="text-base font-semibold text-[var(--color-text-primary)] mb-1">
                Results
              </p>
              <p className="text-xs text-[var(--color-text-tertiary)] mb-4">
                Validation detail for your mapped file against the target schema
                {retrievedResultsByAction
                  ? " (reference-list values normalised to contract form)."
                  : "."}
              </p>

              <div className="mb-6">
                {conformanceFilterEmpty && (
                  <p
                    className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3"
                    role="status"
                  >
                    No rows match the current filter
                    {selectedCheckTypeBuckets.length > 0
                      ? ". Clear selections in the Summary section to show all rows again."
                      : "."}
                  </p>
                )}
                {shouldShowQuarantineReconstructBanner(
                  mappedGrid.fromQuarantine,
                  previewHydrateFailed
                ) && (
                  <p
                    className="text-sm text-blue-900 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-3"
                    role="status"
                  >
                    Showing failing rows reconstructed from the saved DQX job. Edits are recorded to
                    the audit trail; re-validation can only re-run the workflow if the original
                    source file is still available on the backend.
                  </p>
                )}
                <DqxTable
                  headers={headers}
                  rows={rows}
                  columnMappings={[]}
                  validation={validation as DqxValidationResult}
                  visibleRowIndices={conformanceVisibleRowIndices}
                  selectedBuckets={selectedCheckTypeBuckets}
                  contractRuleLookup={dqxContractRuleLookup}
                  columnAllowedValuesLookup={columnAllowedValuesLookup}
                  maxHeight={460}
                  canEdit={canManuallyCleanse}
                  cellEdits={cellEdits}
                  onCellEdited={handleCellEdited}
                  onCellReverted={handleCellReverted}
                />
              </div>

              {rowLevelIssues.length > 0 && (
                <div>
                  <div className="mb-2">
                    <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                      More Information
                    </p>
                    <p className="text-xs text-[var(--color-text-tertiary)] mt-0.5">
                      Additional validation details are listed below.
                    </p>
                  </div>
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 max-h-[min(60vh,28rem)] overflow-y-auto">
                    <div className="space-y-1">
                      {rowLevelIssues.map((issue, idx) => (
                        <div key={`${issue.rowIndex}-${idx}`} className="text-xs text-amber-900">
                          <p>
                            Row {issue.displayRow}: {issue.error}
                            {issue.value?.trim() ? (
                              <span className="ml-2 text-[11px] font-medium text-amber-900/85">
                                (Invalid value: {issue.value})
                              </span>
                            ) : null}
                          </p>
                          {issue.detail ? (
                            <p className="mt-1 pl-2 border-l-2 border-amber-300 text-[11px] leading-snug text-amber-950/90 font-mono">
                              {issue.detail}
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </>
        )}

        {/* Downloads — bottom of page (above floating bar) */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          aria-labelledby="dqx-downloads-heading"
          className="rounded-xl border border-[var(--color-border-light)] bg-[var(--color-bg-secondary)]/60 px-5 py-4 mb-6"
        >
          <h2
            id="dqx-downloads-heading"
            className="text-sm font-semibold text-[var(--color-text-primary)] mb-1"
          >
            Downloads
          </h2>
          <p className="text-xs text-[var(--color-text-tertiary)] mb-4 max-w-2xl leading-relaxed">
            Export your remapped data and a CSV of validation errors (when the assessment found
            issues)
          </p>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="secondary"
              size="md"
              onClick={handleDownloadErrorReport}
              disabled={!hasErrors || !validation}
              className="gap-2"
            >
              Download Error Report
              <Download className="w-4 h-4" />
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={handleDownloadRemapped}
              disabled={!ws}
              className="gap-2"
            >
              Download Remapped File
              <Download className="w-4 h-4" />
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={() => void handleDownloadDataContractRules()}
              disabled={!canDownloadContractRules || downloadingContractRules}
              title={
                canDownloadContractRules
                  ? undefined
                  : "Upload context must include a schema so the server can resolve the contract folder."
              }
              className="gap-2 hidden"
            >
              {downloadingContractRules ? (
                <>
                  <Spinner size="sm" className="!h-4 !w-4" />
                  Preparing…
                </>
              ) : (
                <>
                  Download Data Contract Rules
                  <ScrollText className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </motion.section>

        <ConfirmDialog
          open={pendingEditBlockOpen}
          onOpenChange={setPendingEditBlockOpen}
          title="Re-run Quality Checks first"
          description={
            <>
              You have {pendingEditCount} pending edit{pendingEditCount === 1 ? "" : "s"} that{" "}
              {pendingEditCount === 1 ? "has" : "have"} not been re-validated. Re-run Quality Checks
              to confirm the corrections meet the Data Contract before proceeding.
            </>
          }
          confirmLabel="Re-run now"
          cancelLabel="Keep editing"
          tone="warning"
          onConfirm={() => {
            setPendingEditBlockOpen(false);
            void handleRevalidateWithEdits();
          }}
        />

        {/* Bottom padding for floating bar */}
        <div className="h-24" />

        {/* Floating Action Bar */}
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.35, type: "spring", stiffness: 300, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-[var(--color-border-light)] shadow-2xl"
        >
          <div className="mx-auto max-w-7xl px-6 py-3 flex items-center justify-end gap-3">
            <Button
              variant={hasErrors ? "primary" : "ghost"}
              size="sm"
              onClick={handleBackToUpload}
              className="gap-1.5"
            >
              <ArrowLeft className="w-4 h-4" />
              {dialogInset || autoRetrieve ? "Back to Workspace" : "Back to file upload"}
            </Button>

            <Button
              variant="primary"
              size="sm"
              onClick={handleProceed}
              disabled={!hasValidation || !canProceed}
              className={
                hasValidation && canProceed && pendingEditCount === 0
                  ? "min-w-[120px] ring-2 ring-[var(--color-brand-primary)]/30 hover:ring-4 transition-all duration-300"
                  : "min-w-[120px]"
              }
            >
              Proceed
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </motion.div>
      </div>
    </>
  );
}
